import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Links = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔗 CSS Links</h1>

      {/* Intro Gradient Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #d1fae5, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          ✨ Styling Links with CSS
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          Links are one of the most important elements in a webpage. By default,
          they are blue and underlined. With CSS, you can style links in
          different states like normal, hover, active, and visited.
        </p>
      </div>

      {/* Link States */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Link States</h2>
        <p>
          Links can be styled differently depending on their state using pseudo
          classes:
        </p>
        <ul>
          <li>
            <code>a:link</code> → A normal, unvisited link
          </li>
          <li>
            <code>a:visited</code> → A link the user has already visited
          </li>
          <li>
            <code>a:hover</code> → A link when the mouse is over it
          </li>
          <li>
            <code>a:active</code> → A link when it is being clicked
          </li>
        </ul>
        <CodeCopyBlock
          code={`a:link {
  color: green;
  text-decoration: none;
}

a:visited {
  color: purple;
}

a:hover {
  color: red;
  text-decoration: underline;
}

a:active {
  color: orange;
}`}
        />
        <p>👉 Example preview:</p>
        <a
          href="#"
          style={{
            color: "green",
            textDecoration: "none",
            padding: "4px 8px",
            display: "inline-block",
            border: "1px solid #1f7a39",
            borderRadius: "6px",
            marginRight: "10px",
          }}
        >
          Sample Link
        </a>
      </div>

      {/* Link Buttons */}
      <div className="tutorial-section">
        <h2 className="section-title">🔘 Links as Buttons</h2>
        <p>
          Links can be styled to look like buttons using background colors,
          padding, and border-radius.
        </p>
        <CodeCopyBlock
          code={`a.button {
  background-color: #1f7a39;
  color: white;
  padding: 8px 14px;
  text-decoration: none;
  border-radius: 6px;
}

a.button:hover {
  background-color: #166534;
}`}
        />
        <a
          href="#"
          style={{
            backgroundColor: "#1f7a39",
            color: "white",
            padding: "8px 14px",
            textDecoration: "none",
            borderRadius: "6px",
            marginRight: "10px",
          }}
        >
          Button Link
        </a>
      </div>

      {/* Styling Text Decorations */}
      <div className="tutorial-section">
        <h2 className="section-title">🎨 Removing or Changing Underlines</h2>
        <p>
          By default, links are underlined. You can use{" "}
          <code>text-decoration</code> to remove or customize it.
        </p>
        <CodeCopyBlock
          code={`a {
  text-decoration: none; /* removes underline */
}

a:hover {
  text-decoration: underline; /* adds underline on hover */
}`}
        />
        <a
          href="#"
          style={{
            color: "#1f7a39",
            textDecoration: "none",
            fontWeight: "bold",
          }}
        >
          No underline link
        </a>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices</h2>
        <ul>
          <li>Always make links visually different from normal text.</li>
          <li>Use hover effects to improve user experience.</li>
          <li>
            Avoid removing underlines completely unless links are styled like
            buttons.
          </li>
          <li>Use clear and descriptive link text.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          After learning links, the next topic is{" "}
          <a href="/css/lists" className="learn-more">
            CSS Lists
          </a>{" "}
          to understand how to style unordered and ordered lists.
        </p>
      </div>
    </div>
  );
};

export default CSS_Links;
